import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Globe, Send, CheckCircle, ShieldCheck, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEO, { SITE, breadcrumb, orgRef } from '../components/SEO';

const SERVICE_OPTIONS = [
  'Security Assessment',
  'Vulnerability Management',
  'Identity & Access Security',
  'Endpoint & Network Security',
  'Data Protection & Backup',
  'Security Awareness & Training',
  'General Cybersecurity Consultation',
];

const ContactPage = () => {
  const [form, setForm] = useState({
    fullName: '',
    businessEmail: '',
    phoneNumber: '',
    companyName: '',
    serviceInterest: '',
    message: '',
    website: '', // honeypot
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.fullName,
          business_email: form.businessEmail,
          phone_number: form.phoneNumber,
          company_name: form.companyName,
          service_interest: form.serviceInterest,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success('Message sent. Kenny will be in touch soon.', {
        icon: <CheckCircle className="text-emerald-500" />,
      });
      setForm({ fullName: '', businessEmail: '', phoneNumber: '', companyName: '', serviceInterest: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again or email kenny@kennyvicops.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "h-12 bg-white border-slate-300 text-[#101828] placeholder:text-slate-400 focus-visible:ring-blue-500/30 focus-visible:border-blue-500";
  const labelClass = "text-sm text-[#101828] block mb-2 font-medium";

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path="/contact"
        title="Contact Kennyvicops | Request a Security Consultation"
        description="Have a cybersecurity concern, security gap or project? Contact Kenny Vic at Kennyvicops Cyber Solutions Inc. Phone +1 (437) 933-4970 · kenny@kennyvicops.com."
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Contact', path: '/contact' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            url: `${SITE}/contact`,
            about: orgRef,
          },
        ]}
      />

      {/* HERO — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(22,135,255,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-20">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-cyan-300/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Contact</span>
          </div>
          <h1 className="font-editorial text-4xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-white">
            Let&apos;s talk about your security.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-3xl">
            Have a cybersecurity concern, security gap or project you want to discuss? Let&apos;s start with a conversation.
          </p>
        </div>
      </section>

      {/* METHODS — white, prominent contact info */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2.5 mb-4">
              <span className="w-6 h-px bg-blue-600/70" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">Get in Touch</span>
            </div>
            <h2 className="font-editorial text-3xl sm:text-4xl font-semibold text-[#06101F] leading-[1.1]">
              Reach Kenny directly.
            </h2>
            <p className="mt-4 text-[#475467] text-base sm:text-lg leading-relaxed">
              Prefer a call, an email or a quick self-check? Pick whatever works for you.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3 mb-6">
            {[
              { href: 'tel:+14379334970', icon: Phone, label: 'Call', value: '+1 (437) 933-4970', sub: 'Mon\u2013Fri, business hours', testId: 'contact-phone' },
              { href: 'mailto:kenny@kennyvicops.com', icon: Mail, label: 'Email', value: 'kenny@kennyvicops.com', sub: 'Response within 24 business hours', testId: 'contact-email' },
              { href: 'https://kennyvicops.com', icon: Globe, label: 'Website', value: 'kennyvicops.com', sub: 'Explore services and insights', testId: 'contact-website', ext: true },
            ].map((m) => (
              <a
                key={m.testId}
                href={m.href}
                {...(m.ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                data-testid={m.testId}
                className="group bg-white border border-slate-200 hover:border-blue-500/60 hover:shadow-lg hover:shadow-slate-900/5 hover:-translate-y-0.5 rounded-2xl p-6 sm:p-7 flex flex-col transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl group-hover:bg-blue-100 transition-colors">
                    <m.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">{m.label}</span>
                </div>
                <p className="text-lg sm:text-xl text-[#06101F] font-semibold break-all leading-tight">{m.value}</p>
                <p className="mt-2 text-sm text-[#475467]">{m.sub}</p>
              </a>
            ))}
          </div>

          <Link
            to="/security-health-check"
            data-testid="contact-healthcheck-link"
            className="group flex items-center justify-between gap-4 p-5 bg-[#F5F8FC] border border-slate-200 hover:border-blue-500/50 hover:bg-white hover:shadow-md hover:shadow-slate-900/5 rounded-2xl transition-all"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-blue-700 font-semibold mb-1">Free Tool &middot; 60 seconds</div>
              <p className="text-sm sm:text-base text-[#101828] font-semibold">
                Prefer to see where you stand first? Take the free Security Health Check.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </Link>
        </div>
      </section>

      {/* FORM — off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-xl mb-8">
            <div className="inline-flex items-center gap-2.5 mb-4">
              <span className="w-6 h-px bg-blue-600/70" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">Send a Message</span>
            </div>
            <h2 className="font-editorial text-3xl sm:text-4xl font-semibold text-[#06101F] leading-[1.1]">
              Tell us a little about your situation.
            </h2>
            <p className="mt-4 text-[#475467] text-base sm:text-lg leading-relaxed">
              We&apos;ll get back to you personally.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-sm">
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Full Name *</label>
                  <Input name="fullName" required value={form.fullName} onChange={onChange} data-testid="contact-form-name" className={inputClass} placeholder="Kenny Vic" />
                </div>
                <div>
                  <label className={labelClass}>Business Email *</label>
                  <Input type="email" name="businessEmail" required value={form.businessEmail} onChange={onChange} data-testid="contact-form-email" className={inputClass} placeholder="name@company.com" />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Phone Number</label>
                  <Input type="tel" name="phoneNumber" value={form.phoneNumber} onChange={onChange} data-testid="contact-form-phone" className={inputClass} placeholder="+1 (___) ___-____" />
                </div>
                <div>
                  <label className={labelClass}>Company Name *</label>
                  <Input name="companyName" required value={form.companyName} onChange={onChange} data-testid="contact-form-company" className={inputClass} placeholder="Your business" />
                </div>
              </div>
              <div>
                <label className={labelClass}>Service Interest *</label>
                <select
                  name="serviceInterest"
                  required
                  value={form.serviceInterest}
                  onChange={onChange}
                  data-testid="contact-form-interest"
                  className="w-full h-12 bg-white border border-slate-300 text-[#101828] rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                >
                  <option value="">Select a service</option>
                  {SERVICE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Message *</label>
                <Textarea
                  name="message" required rows={6} maxLength={4000}
                  value={form.message} onChange={onChange}
                  data-testid="contact-form-message"
                  className="bg-white border-slate-300 text-[#101828] placeholder:text-slate-400 focus-visible:ring-blue-500/30 focus-visible:border-blue-500 resize-none"
                  placeholder="Tell us about your environment, concerns or the project you have in mind…"
                />
              </div>

              {/* Honeypot */}
              <div className="hidden" aria-hidden="true">
                <label>Website</label>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
              </div>

              <div
                className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs sm:text-sm text-amber-900 leading-relaxed"
                data-testid="contact-security-notice"
              >
                <ShieldCheck className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-semibold">Security notice:</span>{' '}
                  Please do not submit passwords, credentials, security keys, confidential incident details, or other sensitive security information through this form.
                </span>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-form-submit"
                className="w-full py-6 bg-[#06101F] hover:bg-[#0B1B35] text-white text-base rounded-full font-semibold"
              >
                {submitting ? 'Sending…' : (<><Send className="w-4 h-4 mr-2" /> Send Message</>)}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                We respect your privacy. Your details are only used to respond to your enquiry.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
